// Jun-2026 — Phase 3C: Project GRN tab (register + create/edit + detail +
// submit / cancel / reverse / recover). Accepted quantities become LOCKED
// project stock on final approval; PI and consumption remain blocked.
// Jun-2026 (owner-authorized) — multi-split receiving: several receipt rows
// per PO line ("Add More Quantity"), each with optional Category 1/2 from
// admin-manageable masters (ID-bound, snapshot retained on the line).
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Plus, X, RefreshCw, Eye, Send, Ban, Paperclip, History, Download,
  ShieldAlert, Undo2, LifeBuoy, Lock, Pencil, Camera, FolderOpen, FileEdit,
  Tags,
} from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { EvidencePhotos } from "@/components/EvidencePhotos";
import GrnAmendModal from "@/components/GrnAmendModal";
import GrnCategoryMasterModal from "@/components/GrnCategoryMasterModal";
import { compressImage } from "@/lib/imageCompress";

const S_BADGE = {
  Draft: "bg-amber-100 text-amber-800 border-amber-300",
  "Pending Approval": "bg-sky-100 text-sky-800 border-sky-300",
  Posting: "bg-indigo-100 text-indigo-800 border-indigo-300",
  Approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Rejected: "bg-rose-100 text-rose-800 border-rose-300",
  Cancelled: "bg-slate-200 text-slate-600 border-slate-300",
  Reversing: "bg-orange-100 text-orange-800 border-orange-300",
  Reversed: "bg-orange-100 text-orange-800 border-orange-300",
  "Posting Failed": "bg-red-100 text-red-800 border-red-300",
};
const STATUSES = ["", "Draft", "Pending Approval", "Approved", "Rejected",
  "Cancelled", "Reversed", "Posting Failed"];

const Badge = ({ s }) => (
  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border rounded ${S_BADGE[s] || "bg-slate-100 text-slate-600 border-slate-300"}`}>{s}</span>
);

const inputCls = "w-full border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#F97316]";
const numCls = "w-20 border border-slate-300 rounded px-1.5 py-1 text-xs text-right focus:outline-none focus:border-[#F97316]";
const round6 = (n) => Math.round(Number(n || 0) * 1e6) / 1e6;

function CatSelect({ kind, list, value, snapName, testid, onChange }) {
  const inList = (list || []).some((c) => c.id === value);
  return (
    <select data-testid={testid} className={inputCls} value={value || ""}
      onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      {value && !inList && <option value={value}>{snapName || "(unknown)"} · inactive</option>}
      {(list || []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
    </select>
  );
}

function SplitRow({ l, s, i, cats, onPatch, onRemove }) {
  const sum = Number(s.accepted_qty || 0) + Number(s.rejected_qty || 0) + Number(s.damaged_qty || 0);
  const eqOk = Math.abs(sum - Number(s.received_qty || 0)) < 1e-6;
  const uid = l.po_line_uid;
  const num = (field, testid) => (
    <input data-testid={testid} className={numCls} type="number" min="0"
      value={s[field] ?? ""} onChange={(e) => onPatch({ [field]: e.target.value })} />
  );
  return (
    <div className="px-3 py-2 border-t border-slate-100" data-testid={`grn-split-row-${uid}-${i}`}>
      <div className="flex items-end gap-2 flex-wrap">
        <div><div className="text-[9px] uppercase text-slate-400">Received *</div>{num("received_qty", `grn-split-received-${uid}-${i}`)}</div>
        <div><div className="text-[9px] uppercase text-slate-400">Accepted *</div>{num("accepted_qty", `grn-split-accepted-${uid}-${i}`)}</div>
        <div><div className="text-[9px] uppercase text-slate-400">Rejected</div>{num("rejected_qty", `grn-split-rejected-${uid}-${i}`)}</div>
        <div><div className="text-[9px] uppercase text-slate-400">Damaged</div>{num("damaged_qty", `grn-split-damaged-${uid}-${i}`)}</div>
        <div className="w-40"><div className="text-[9px] uppercase text-slate-400">Category 1</div>
          <CatSelect kind="cat1" list={cats.cat1} value={s.category1_id} snapName={s.category1_name}
            testid={`grn-split-cat1-${uid}-${i}`} onChange={(v) => onPatch({ category1_id: v })} /></div>
        <div className="w-40"><div className="text-[9px] uppercase text-slate-400">Category 2</div>
          <CatSelect kind="cat2" list={cats.cat2} value={s.category2_id} snapName={s.category2_name}
            testid={`grn-split-cat2-${uid}-${i}`} onChange={(v) => onPatch({ category2_id: v })} /></div>
        <div className="flex-1 min-w-[140px]"><div className="text-[9px] uppercase text-slate-400">Storage / Remarks</div>
          <input data-testid={`grn-split-storage-${uid}-${i}`} className={inputCls} placeholder="storage / inspection note"
            value={s.storage_location ?? ""} onChange={(e) => onPatch({ storage_location: e.target.value })} /></div>
        <button type="button" data-testid={`grn-split-remove-${uid}-${i}`} onClick={onRemove}
          className="p-1.5 text-rose-500 hover:text-rose-700 mb-0.5" title="Remove this receipt row"><X size={13} /></button>
      </div>
      {!eqOk && <div className="text-[10px] text-rose-600 mt-0.5">Accepted + Rejected + Damaged must equal Received</div>}
    </div>
  );
}

function GRNForm({ project, existing, onClose, onSaved }) {
  const pid = project.id;
  const [pos, setPos] = useState([]);
  const [poId, setPoId] = useState(existing?.po_id || "");
  const [warehouses, setWarehouses] = useState([]);
  const [cats, setCats] = useState({ cat1: [], cat2: [] });
  const [head, setHead] = useState({
    grn_date: existing?.grn_date || new Date().toISOString().slice(0, 10),
    warehouse_id: existing?.warehouse_id || "",
    challan_no: existing?.challan_no || "",
    challan_date: existing?.challan_date || "",
    vehicle_no: existing?.vehicle_no || "",
    received_by: existing?.received_by || "",
    quality_remarks: existing?.quality_remarks || "",
  });
  // Multi-split: ordered array of receipt rows; several rows may reference
  // the same po_line_uid. Existing rows keep their stable grn_line_uid.
  const [splits, setSplits] = useState([]);
  const [saving, setSaving] = useState(false);
  // Jun-2026 (user request) — attach files while CREATING the GRN.
  // Staged locally, uploaded to the draft right after it is created.
  const [attachments, setAttachments] = useState([]);   // File objects

  const ATT_MAX = 500 * 1024;
  // Aug-2026 (user request) — Gallery/File picker allows MULTI-select;
  // every picked file is staged (images auto-compressed) in one go.
  const addAttachment = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      let staged = f;
      if (/^image\//.test(f.type || "")) {
        staged = await compressImage(f);         // shrink to ≤500 KB client-side
        if (staged.size > ATT_MAX) {
          toast.error(`${f.name}: image could not be reduced below 500 KB`);
          continue;
        }
      } else if (!/\.pdf$/i.test(f.name || "") && f.size > ATT_MAX) {
        // PDFs above 500 KB are allowed through — the server attempts
        // compression and rejects with a clear message if still too big.
        toast.error(`${f.name}: file exceeds 500 KB — max 500 KB per attachment`);
        continue;
      }
      setAttachments((ls) => [...ls, staged]);
    }
  };
  const removeAttachment = (idx) =>
    setAttachments((ls) => ls.filter((_, i) => i !== idx));

  useEffect(() => {
    api.get(`/projects/${pid}/grn/eligible-pos`)
      .then((r) => setPos(r.data?.pos || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load eligible POs"));
  }, [pid]);

  useEffect(() => {
    api.get(`/grn-categories`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setCats({ cat1: list.filter((c) => c.kind === "cat1"),
                  cat2: list.filter((c) => c.kind === "cat2") });
      })
      .catch(() => {});
  }, []);

  const po = pos.find((p) => p.po_id === poId);

  useEffect(() => {
    if (!po) return;
    api.get(`/warehouses`, { params: { branch_id: po.branch_id, limit: 500 } })
      .then((r) => setWarehouses(Array.isArray(r.data) ? r.data : (r.data?.rows || [])))
      .catch(() => setWarehouses([]));
    if (existing && existing.po_id === po.po_id) {
      setSplits((existing.line_items || []).map((l) => ({
        key: l.grn_line_uid,
        grn_line_uid: l.grn_line_uid,
        po_line_uid: l.po_line_uid,
        received_qty: l.received_qty, accepted_qty: l.accepted_qty,
        rejected_qty: l.rejected_qty, damaged_qty: l.damaged_qty,
        storage_location: l.storage_location || "",
        inspection_remarks: l.inspection_remarks || "",
        category1_id: l.category1_id || "", category1_name: l.category1_name || "",
        category2_id: l.category2_id || "", category2_name: l.category2_name || "",
      })));
    } else {
      setSplits([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId, pos.length]);

  const lineOf = (uid) => (po?.lines || []).find((l) => l.po_line_uid === uid);
  const accSum = (uid) => splits.filter((s) => s.po_line_uid === uid)
    .reduce((t, s) => t + Number(s.accepted_qty || 0), 0);
  const patchSplit = (key, patch) =>
    setSplits((ls) => ls.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const removeSplit = (key) => setSplits((ls) => ls.filter((s) => s.key !== key));
  const addSplit = (l) => {
    const bal = Math.max(0, round6(l.remaining_qty - accSum(l.po_line_uid)));
    setSplits((ls) => [...ls, {
      key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      po_line_uid: l.po_line_uid, received_qty: bal, accepted_qty: bal,
      rejected_qty: 0, damaged_qty: 0, storage_location: "",
      inspection_remarks: "", category1_id: "", category2_id: "" }]);
  };

  const save = async () => {
    if (!po) { toast.error("Select an eligible Project Operations PO"); return; }
    if (!splits.length) { toast.error("Add at least one receipt row"); return; }
    for (const l of po.lines || []) {
      const tot = round6(accSum(l.po_line_uid));
      if (tot > l.remaining_qty + 1e-6) {
        toast.error(`${l.item_name}: combined accepted quantity ${tot} exceeds the balance PO quantity ${l.remaining_qty}`);
        return;
      }
    }
    const line_items = splits.map((s) => ({
      po_line_uid: s.po_line_uid,
      ...(s.grn_line_uid ? { grn_line_uid: s.grn_line_uid } : {}),
      uom: lineOf(s.po_line_uid)?.uom || "",
      received_qty: Number(s.received_qty || 0),
      accepted_qty: Number(s.accepted_qty || 0),
      rejected_qty: Number(s.rejected_qty || 0),
      damaged_qty: Number(s.damaged_qty || 0),
      storage_location: s.storage_location || "",
      inspection_remarks: s.inspection_remarks || "",
      category1_id: s.category1_id || "",
      category2_id: s.category2_id || "",
    }));
    setSaving(true);
    try {
      const payload = { ...head, po_id: po.po_id, line_items };
      let grnId = existing?.id;
      if (existing) await api.put(`/projects/${pid}/grns/${existing.id}`, payload);
      else {
        const r = await api.post(`/projects/${pid}/grns`, payload);
        grnId = r.data?.id;
      }
      // Upload staged attachments to the draft (each ≤500 KB; server
      // recompresses oversized images / PDFs and rejects the rest).
      let attFailed = 0;
      for (const f of attachments) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          await api.post(`/projects/${pid}/grns/${grnId}/attachments`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
        } catch (err) {
          attFailed += 1;
          toast.error(`${f.name}: ${err?.response?.data?.detail || "attachment upload failed"}`);
        }
      }
      toast.success((existing ? "GRN draft updated" : "GRN draft created")
        + (attachments.length ? ` · ${attachments.length - attFailed}/${attachments.length} attachment(s) uploaded` : ""));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save GRN");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-8" data-testid="grn-form-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">{existing ? `Edit GRN ${existing.grn_number}` : "New Project GRN"}</div>
          <button data-testid="grn-form-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-3">
              <label className="text-[11px] font-bold text-slate-600">Approved Project PO *</label>
              <SearchableSelect testid="grn-po-select" items={pos} value={poId}
                disabled={!!existing} onChange={(id) => setPoId(id)}
                getKey={(p) => p.po_id}
                getLabel={(p) => `${p.po_number} · ${p.vendor_name} · ${p.material_receipt_status}`}
                getSubLabel={(p) => `${p.po_date || ""} · ${(p.lines || []).length} GRN-routed line(s)`}
                getSearchText={(p) => `${p.po_number} ${p.vendor_name} ${p.material_receipt_status}`}
                placeholder="— search eligible PO by number / vendor —"
                searchPlaceholder="Type PO number or vendor…" />
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-600">GRN Date *</label>
              <input data-testid="grn-date-input" type="date" className={inputCls} value={head.grn_date}
                onChange={(e) => setHead({ ...head, grn_date: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-600">Storage Warehouse *</label>
              <select data-testid="grn-warehouse-select" className={inputCls} value={head.warehouse_id}
                onChange={(e) => setHead({ ...head, warehouse_id: e.target.value })}>
                <option value="">— select —</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>{w.warehouse_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-600">Received By</label>
              <input data-testid="grn-received-by-input" className={inputCls} value={head.received_by}
                onChange={(e) => setHead({ ...head, received_by: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-600">Vendor Challan No</label>
              <input data-testid="grn-challan-no-input" className={inputCls} value={head.challan_no}
                onChange={(e) => setHead({ ...head, challan_no: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-600">Challan Date</label>
              <input type="date" className={inputCls} value={head.challan_date}
                onChange={(e) => setHead({ ...head, challan_date: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-600">Vehicle No</label>
              <input data-testid="grn-vehicle-input" className={inputCls} value={head.vehicle_no}
                onChange={(e) => setHead({ ...head, vehicle_no: e.target.value })} />
            </div>
            <div className="sm:col-span-3">
              <label className="text-[11px] font-bold text-slate-600">Quality Remarks</label>
              <input className={inputCls} value={head.quality_remarks}
                onChange={(e) => setHead({ ...head, quality_remarks: e.target.value })} />
            </div>
          </div>

          {po && (
            <div className="space-y-2" data-testid="grn-form-lines">
              {(po.lines || []).map((l) => {
                const lineRows = splits
                  .map((s, gi) => ({ s, gi }))
                  .filter(({ s }) => s.po_line_uid === l.po_line_uid);
                const cur = round6(accSum(l.po_line_uid));
                const bal = round6(l.remaining_qty - cur);
                return (
                  <div key={l.po_line_uid} className="border border-slate-200 rounded" data-testid={`grn-po-line-${l.po_line_uid}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap px-3 py-2 bg-slate-50 border-b border-slate-100">
                      <div className="text-xs font-bold text-slate-800">
                        {l.item_name} <span className="text-slate-400 font-mono font-normal">{l.item_code}</span>
                        <span className="ml-1 text-slate-500 font-normal">· {l.uom}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                        <span className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded">Ordered <b>{l.approved_po_qty}</b></span>
                        <span className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded">Prev Received <b>{l.prev_accepted_qty}</b></span>
                        <span className="px-1.5 py-0.5 bg-sky-50 border border-sky-200 rounded text-sky-800" data-testid={`grn-line-current-${l.po_line_uid}`}>This GRN <b>{cur}</b></span>
                        <span className={`px-1.5 py-0.5 border rounded ${bal < -1e-6 ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} data-testid={`grn-line-balance-${l.po_line_uid}`}>Balance <b>{bal}</b></span>
                        <button type="button" data-testid={`grn-line-add-split-${l.po_line_uid}`}
                          disabled={l.remaining_qty <= 0 && lineRows.length === 0}
                          onClick={() => addSplit(l)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-[#F97316] border border-orange-300 rounded hover:bg-orange-50 disabled:opacity-40">
                          <Plus size={11} /> {lineRows.length ? "Add More Quantity" : "Add Quantity"}
                        </button>
                      </div>
                    </div>
                    {lineRows.length === 0
                      ? <div className="px-3 py-2 text-[11px] text-slate-400" data-testid={`grn-line-empty-${l.po_line_uid}`}>Not receiving this line in the current GRN — use “Add Quantity”</div>
                      : lineRows.map(({ s }, i) => (
                        <SplitRow key={s.key} l={l} s={s} i={i} cats={cats}
                          onPatch={(p) => patchSplit(s.key, p)}
                          onRemove={() => removeSplit(s.key)} />
                      ))}
                    {bal < -1e-6 && (
                      <div className="px-3 pb-2 text-[10px] text-rose-600 font-bold" data-testid={`grn-line-over-${l.po_line_uid}`}>
                        Combined accepted quantity exceeds the balance PO quantity
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="text-[11px] text-slate-500">
            Only <b>Accepted</b> quantity enters Project Stock (locked). Rejected/Damaged stay on GRN history.
            The same PO line can be received in multiple rows (e.g. different categories) — the combined quantity can never exceed the PO balance;
            the server re-verifies atomically at approval time.
          </div>

          {/* Attachments — camera / gallery-or-file, ≤500 KB each */}
          <div className="border-t border-slate-100 pt-3" data-testid="grn-form-attachments">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1">
                <Paperclip size={11} /> Attachments <span className="normal-case font-normal">(max 500 KB each — larger images are compressed automatically)</span>
              </div>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold text-[#F97316] border border-orange-300 rounded cursor-pointer hover:bg-orange-50"
                  data-testid="grn-form-attach-camera-label">
                  <Camera size={12} /> Camera
                  <input data-testid="grn-form-attach-camera" type="file" accept="image/*" capture="environment"
                    className="hidden" onChange={addAttachment} />
                </label>
                <label className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold text-[#F97316] border border-orange-300 rounded cursor-pointer hover:bg-orange-50"
                  data-testid="grn-form-attach-file-label">
                  <FolderOpen size={12} /> Gallery / File
                  <input data-testid="grn-form-attach-file" type="file" accept="image/*,.pdf,.xlsx,.docx" multiple
                    className="hidden" onChange={addAttachment} />
                </label>
              </div>
            </div>
            {attachments.length === 0
              ? <div className="text-[11px] text-slate-400 mt-1" data-testid="grn-form-attach-empty">No attachments — use Camera or Gallery / File to add (photo, PDF, Excel or Word)</div>
              : attachments.map((f, i) => (
                <div key={i} className="flex items-center gap-2 mt-1 text-xs" data-testid={`grn-form-attach-row-${i}`}>
                  <Paperclip size={11} className="text-slate-400" />
                  <span className="text-slate-800 truncate max-w-[280px]">{f.name}</span>
                  <span className="text-slate-400 text-[10px]">{(f.size / 1024).toFixed(0)} KB</span>
                  <button data-testid={`grn-form-attach-remove-${i}`} onClick={() => removeAttachment(i)}
                    className="text-rose-600 hover:text-rose-800" title="Remove"><X size={12} /></button>
                </div>
              ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button data-testid="grn-form-cancel" onClick={onClose} className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
          <button data-testid="grn-form-save" onClick={save} disabled={saving}
            className="px-4 py-1.5 text-xs font-bold text-white bg-[#F97316] rounded hover:bg-orange-600 disabled:opacity-50">
            {saving ? "Saving…" : existing ? "Update Draft" : "Create Draft GRN"}
          </button>
        </div>
      </div>
    </div>
  );
}

function GRNDetail({ project, grn, onClose, onChanged, onEdit, onAmend }) {
  const pid = project.id;
  const { user, hasPerm } = useAuth();
  const isMaker = user?.role === "admin" || grn.created_by === user?.email;
  const values = !!grn.values_visible;

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const submit = async () => {
    try {
      const r = await api.post(`/projects/${pid}/grns/${grn.id}/submit`);
      toast.success(r.data?.auto_approved
        ? "GRN approved — no approval matrix configured, stock locked in"
        : "GRN submitted for approval");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const recallEdit = async () => {
    try {
      await api.post(`/projects/${pid}/grns/${grn.id}/recall`);
      toast.success("GRN recalled to Draft — edit and resubmit");
      onEdit();
    } catch (e) { toast.error(e?.response?.data?.detail || "Recall failed"); }
  };
  const cancelAmendment = () => {
    if (!window.confirm("Withdraw the pending amendment? No stock was changed.")) return;
    act(() => api.post(`/projects/${pid}/grns/${grn.id}/amend/cancel`), "Amendment cancelled");
  };
  const cancel = () => {
    const reason = window.prompt("Cancellation reason (mandatory):");
    if (!reason) return;
    act(() => api.post(`/projects/${pid}/grns/${grn.id}/cancel`, { reason }), "GRN cancelled");
  };
  const recover = () => act(() => api.post(`/projects/${pid}/grns/${grn.id}/recover`), "Posting recovered — GRN approved");
  const reverse = async () => {
    if (grn.status !== "Reversing") {
      try {
        const chk = await api.get(`/projects/${pid}/grns/${grn.id}/reversal-check`);
        if (!chk.data?.reversible) {
          toast.error("Reversal blocked: " + (chk.data?.reasons || []).join("; "));
          return;
        }
      } catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); return; }
    }
    const reason = window.prompt("Reversal reason (mandatory):");
    if (!reason) return;
    act(() => api.post(`/projects/${pid}/grns/${grn.id}/reverse`, { reason }),
      "GRN reversed — compensating entries recorded; original records retained");
  };
  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    // ≤500 KB rule — compress images client-side before hitting the wire.
    const staged = /^image\//.test(f.type || "") ? await compressImage(f) : f;
    const fd = new FormData();
    fd.append("file", staged);
    try {
      await api.post(`/projects/${pid}/grns/${grn.id}/attachments`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded"); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-8" data-testid="grn-detail-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold font-mono text-slate-900" data-testid="grn-detail-number">{grn.grn_number}</span>
            <Badge s={grn.status} />
          </div>
          <button data-testid="grn-detail-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 text-xs">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><div className="text-[10px] uppercase text-slate-400">GRN Date</div>{grn.grn_date}</div>
            <div><div className="text-[10px] uppercase text-slate-400">PO</div><span className="font-mono">{grn.po_number}</span></div>
            <div><div className="text-[10px] uppercase text-slate-400">Vendor</div>{grn.vendor_name}</div>
            <div><div className="text-[10px] uppercase text-slate-400">Warehouse</div>{grn.warehouse_name}</div>
            <div><div className="text-[10px] uppercase text-slate-400">Challan</div>{grn.challan_no || "—"} {grn.challan_date}</div>
            <div><div className="text-[10px] uppercase text-slate-400">Vehicle</div>{grn.vehicle_no || "—"}</div>
            <div><div className="text-[10px] uppercase text-slate-400">Received By</div>{grn.received_by || "—"}</div>
            <div><div className="text-[10px] uppercase text-slate-400">Created By</div>{grn.created_by}</div>
          </div>
          {grn.status === "Posting Failed" && (
            <div className="border border-red-300 bg-red-50 rounded p-3 flex items-start gap-2" data-testid="grn-posting-error">
              <ShieldAlert size={14} className="text-red-600 mt-0.5" />
              <div><b>Posting interrupted:</b> {grn.posting_error || "unknown"} — use Recover (authorized approver) or re-approve from the inbox; resume is idempotent.</div>
            </div>
          )}
          {grn.status === "Reversed" && (
            <div className="border border-orange-300 bg-orange-50 rounded p-3 flex items-start gap-2" data-testid="grn-reversed-banner">
              <Undo2 size={14} className="text-orange-600 mt-0.5" />
              <div>
                <b>Reversed (append-only):</b> {grn.reversal_reason || "—"}
                <span className="text-slate-500"> — by {grn.reversed_by} on {(grn.reversed_at || "").slice(0, 10)}.</span>{" "}
                Original receipt, batch, stock entry and movement are retained as historical evidence;
                a compensating reversal entry and movement net the stock to zero.
              </div>
            </div>
          )}
          {grn.amendment_pending && grn.amendment && (
            <div className="border border-amber-300 bg-amber-50 rounded p-3" data-testid="grn-amendment-banner">
              <div className="flex items-start gap-2">
                <FileEdit size={14} className="text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <b>Amendment pending approval</b> — requested by {grn.amendment.requested_by} on {(grn.amendment.requested_at || "").slice(0, 10)}.
                  <span className="text-slate-600"> Reason: {grn.amendment.reason}</span>
                  <div className="mt-1 space-y-0.5">
                    {(grn.amendment.lines || []).map((al) => (
                      <div key={al.grn_line_uid} className="text-[11px] text-slate-700">
                        {al.item_name}: accepted <b>{al.old.accepted_qty} → {al.new.accepted_qty}</b>
                        {" "}· rejected {al.old.rejected_qty} → {al.new.rejected_qty}
                        {" "}· damaged {al.old.damaged_qty} → {al.new.damaged_qty}
                      </div>
                    ))}
                    {Object.keys(grn.amendment.header_new || {}).length > 0 && (
                      <div className="text-[11px] text-slate-700">
                        Header: {Object.entries(grn.amendment.header_new).map(([k, v]) => `${k} → "${v}"`).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">Stock stays unchanged until final approval. Reversal is blocked while the amendment is open.</div>
                </div>
                {isMaker && hasPerm("project_grn", "edit") && (
                  <button data-testid="grn-amend-withdraw-btn" onClick={cancelAmendment}
                    className="text-[11px] font-bold text-rose-700 border border-rose-300 rounded px-2 py-1 hover:bg-rose-50 whitespace-nowrap">
                    Cancel Amendment
                  </button>
                )}
              </div>
            </div>
          )}
          {(grn.amendment_history || []).length > 0 && (
            <div className="border border-slate-200 rounded p-3" data-testid="grn-amendment-history">
              <div className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1 mb-1"><FileEdit size={11} /> Amendments</div>
              {(grn.amendment_history || []).slice().reverse().map((h) => (
                <div key={h.id} className="text-[11px] text-slate-600">
                  <b className={h.status === "Applied" ? "text-emerald-700" : h.status === "Rejected" ? "text-rose-700" : "text-slate-500"}>{h.status}</b>
                  {" "}· {h.reason} <span className="text-slate-400">— {h.requested_by}, {(h.applied_at || h.rejected_at || h.closed_at || h.requested_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full min-w-[900px] text-xs" data-testid="grn-detail-lines-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-2 text-left">Item</th>
                  <th className="px-2 py-2 text-left">UOM</th>
                  <th className="px-2 py-2 text-right">Received</th>
                  <th className="px-2 py-2 text-right">Accepted</th>
                  <th className="px-2 py-2 text-right">Rejected</th>
                  <th className="px-2 py-2 text-right">Damaged</th>
                  <th className="px-2 py-2 text-left">Cat 1</th>
                  <th className="px-2 py-2 text-left">Cat 2</th>
                  {values && <th className="px-2 py-2 text-right">Prov. Rate</th>}
                  {values && <th className="px-2 py-2 text-right">Prov. Value</th>}
                  <th className="px-2 py-2 text-left">Batch</th>
                </tr>
              </thead>
              <tbody>
                {(grn.line_items || []).map((l) => (
                  <tr key={l.grn_line_uid} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">{l.item_name} <span className="text-slate-400 font-mono">{l.item_code}</span></td>
                    <td className="px-2 py-1.5">{l.uom}</td>
                    <td className="px-2 py-1.5 text-right">{l.received_qty}</td>
                    <td className="px-2 py-1.5 text-right font-bold">{l.accepted_qty}</td>
                    <td className="px-2 py-1.5 text-right">{l.rejected_qty}</td>
                    <td className="px-2 py-1.5 text-right">{l.damaged_qty}</td>
                    <td className="px-2 py-1.5" data-testid={`grn-detail-cat1-${l.grn_line_uid}`}>
                      {l.category1_name ? <span title={l.category1_code}>{l.category1_name}</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5" data-testid={`grn-detail-cat2-${l.grn_line_uid}`}>
                      {l.category2_name ? <span title={l.category2_code}>{l.category2_name}</span> : <span className="text-slate-400">—</span>}
                    </td>
                    {values && <td className="px-2 py-1.5 text-right">{l.provisional_rate}</td>}
                    {values && <td className="px-2 py-1.5 text-right">{l.provisional_value}</td>}
                    <td className="px-2 py-1.5">
                      {l.inventory_batch_id
                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><Lock size={11} /> Locked stock</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {values && <div className="text-[11px] text-slate-500 italic">{grn.value_note}</div>}

          <div>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1"><Paperclip size={11} /> Attachments</div>
              {["Draft", "Rejected"].includes(grn.status) && isMaker && (
                <label className="text-[11px] font-bold text-[#F97316] cursor-pointer hover:underline" data-testid="grn-attach-upload-label">
                  + Upload<input type="file" className="hidden" onChange={uploadFile} data-testid="grn-attach-input" />
                </label>
              )}
            </div>
            {(grn.attachments || []).length === 0
              ? <div className="text-slate-400 mt-1">No attachments</div>
              : (grn.attachments || []).map((a) => (
                <div key={a.id} className="flex items-center gap-2 mt-1">
                  <Download size={11} className="text-slate-400" />
                  <a className="text-sky-700 hover:underline" data-testid={`grn-attach-${a.id}`}
                    href="#/" onClick={async (ev) => { ev.preventDefault();
                      const res = await api.get(`/projects/${pid}/grns/${grn.id}/attachments/${a.id}`, { responseType: "blob" });
                      const url = URL.createObjectURL(res.data);
                      const link = document.createElement("a");
                      link.href = url; link.download = a.filename; link.click();
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    }}>{a.filename}</a>
                  <span className="text-slate-400 text-[10px]">{(a.size / 1024).toFixed(0)} KB</span>
                </div>
              ))}
          </div>

          <div className="border-t border-slate-100 pt-3">
            <EvidencePhotos title="Site Photos (evidence)" photos={grn.photos || []} max={6}
              canUpload={hasPerm("project_grn", "edit") && !["Cancelled", "Reversed", "Reversing"].includes(grn.status)}
              uploadUrl={`/projects/${pid}/grns/${grn.id}/photos`}
              photoUrl={(id) => `/projects/${pid}/grns/${grn.id}/photos/${id}`}
              onChanged={onChanged} testidPrefix="grn-photos" />
          </div>

          <div>
            <div className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1"><History size={11} /> Status History</div>
            <div className="mt-1 space-y-0.5 max-h-36 overflow-y-auto">
              {(grn.status_history || []).slice().reverse().map((h, i) => (
                <div key={i} className="text-[11px] text-slate-600">
                  <span className="font-mono text-slate-400">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                  {" "}<b>{h.action}</b> {h.from_status && `${h.from_status} → ${h.to_status}`} <span className="text-slate-400">{h.by}</span>
                  {h.comment && <span className="text-slate-500"> · {h.comment}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end flex-wrap gap-2 px-5 py-3 border-t border-slate-200">
          {["Draft", "Rejected"].includes(grn.status) && isMaker && hasPerm("project_grn", "edit") && (
            <>
              <button data-testid="grn-edit-btn" onClick={onEdit}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-slate-700 border border-slate-300 rounded hover:bg-slate-50"><Pencil size={12} /> Edit</button>
              <button data-testid="grn-submit-btn" onClick={submit}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-sky-600 rounded hover:bg-sky-700"><Send size={12} /> Submit for Approval</button>
              <button data-testid="grn-cancel-btn" onClick={cancel}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-rose-700 border border-rose-300 rounded hover:bg-rose-50"><Ban size={12} /> Cancel</button>
            </>
          )}
          {grn.status === "Pending Approval" && isMaker && hasPerm("project_grn", "edit") && (
            <button data-testid="grn-recall-btn" onClick={recallEdit}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-slate-700 border border-slate-300 rounded hover:bg-slate-50">
              <Pencil size={12} /> Recall &amp; Edit
            </button>
          )}
          {grn.status === "Approved" && !grn.amendment_pending && isMaker && hasPerm("project_grn", "edit") && (
            <button data-testid="grn-amend-btn" onClick={onAmend}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-[#F97316] rounded hover:bg-orange-600">
              <FileEdit size={12} /> Amend GRN
            </button>
          )}
          {grn.status === "Posting Failed" && hasPerm("project_grn", "approve") && (
            <button data-testid="grn-recover-btn" onClick={recover}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 rounded hover:bg-indigo-700"><LifeBuoy size={12} /> Recover Posting</button>
          )}
          {["Approved", "Reversing"].includes(grn.status) && hasPerm("project_grn", "delete") && (
            <button data-testid="grn-reverse-btn" onClick={reverse}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-orange-700 border border-orange-300 rounded hover:bg-orange-50"><Undo2 size={12} /> {grn.status === "Reversing" ? "Resume Reversal" : "Reverse GRN"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProjectGRNTab({ project }) {
  const pid = project.id;
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [grnEnabled, setGrnEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [amendId, setAmendId] = useState(null);
  const [showCats, setShowCats] = useState(false);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${pid}/grns`,
        { params: { page, limit, status: status || undefined, q: q || undefined } });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
      setGrnEnabled(!!r.data?.grn_enabled);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load GRN register");
    }
  }, [pid, page, status, q]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/projects/${pid}/grns/${id}`);
      setDetail(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open GRN"); }
  };

  return (
    <div className="space-y-3" data-testid="project-grn-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="grn-search-input" className="border border-slate-300 rounded px-2 py-1.5 text-xs w-full sm:w-56"
            placeholder="Search GRN / PO / vendor / challan / item name / item code…" value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <select data-testid="grn-status-filter" className="border border-slate-300 rounded px-2 py-1.5 text-xs" value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
          <button data-testid="grn-refresh-btn" onClick={load} className="p-1.5 text-slate-500 hover:text-slate-800"><RefreshCw size={14} /></button>
        </div>
        <div className="flex items-center gap-2">
          {hasPerm("project_grn", "approve") && (
            <button data-testid="grn-manage-categories-btn" onClick={() => setShowCats(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-slate-700 border border-slate-300 rounded hover:bg-slate-50">
              <Tags size={13} /> Categories
            </button>
          )}
          {hasPerm("project_grn", "create") && (
            grnEnabled ? (
              <button data-testid="grn-new-btn" onClick={() => { setEditing(null); setShowForm(true); }}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-[#F97316] rounded hover:bg-orange-600">
                <Plus size={13} /> New GRN
              </button>
            ) : (
              <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="grn-flag-off-note">
                Project GRN receiving is not enabled by the administrator.
              </span>
            )
          )}
        </div>
      </div>

      <div className="hidden md:block border border-slate-200 rounded overflow-x-auto">
        <table className="w-full min-w-[860px] text-xs" data-testid="grn-register-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">GRN Number</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">PO</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-right">Lines</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Level</th>
              <th className="px-3 py-2 text-left">Created By</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400" data-testid="grn-register-empty">No Project GRNs yet</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`grn-row-${r.grn_number}`}>
                <td className="px-3 py-2 font-mono font-bold">{r.grn_number}</td>
                <td className="px-3 py-2">{r.grn_date}</td>
                <td className="px-3 py-2 font-mono">{r.po_number}</td>
                <td className="px-3 py-2">{r.vendor_name}</td>
                <td className="px-3 py-2 text-right">{(r.line_items || []).length}</td>
                <td className="px-3 py-2"><Badge s={r.status} />
                  {r.amendment_pending && (
                    <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 bg-amber-100 text-amber-800" data-testid={`grn-amd-chip-${r.grn_number}`}>AMD</span>
                  )}
                </td>
                <td className="px-3 py-2">{r.approval_level}</td>
                <td className="px-3 py-2">{r.created_by}</td>
                <td className="px-3 py-2 text-right">
                  <button data-testid={`grn-view-${r.grn_number}`} onClick={() => openDetail(r.id)}
                    className="text-sky-700 hover:underline inline-flex items-center gap-1"><Eye size={12} /> View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked GRN cards */}
      <div className="md:hidden border border-slate-200 rounded divide-y divide-slate-100" data-testid="grn-register-cards">
        {rows.length === 0 && (
          <div className="px-3 py-6 text-center text-slate-400 text-xs">No Project GRNs yet</div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="p-3 space-y-1.5" data-testid={`grn-card-${r.grn_number}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[11px] font-bold">{r.grn_number}</span>
              <Badge s={r.status} />
              {r.amendment_pending && (
                <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 bg-amber-100 text-amber-800">AMD</span>
              )}
              <span className="ml-auto text-[10px] text-slate-400">{r.grn_date}</span>
            </div>
            <div className="text-xs text-slate-700 break-words">{r.vendor_name} · <span className="font-mono text-[10px]">{r.po_number}</span></div>
            <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-3">
              <span>Lines: {(r.line_items || []).length}</span>
              <span>Level: {r.approval_level}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] text-slate-400 break-all">{r.created_by}</span>
              <button data-testid={`grn-view-m-${r.grn_number}`} onClick={() => openDetail(r.id)}
                className="text-sky-700 hover:underline inline-flex items-center gap-1 text-xs font-bold shrink-0"><Eye size={12} /> View</button>
            </div>
          </div>
        ))}
      </div>
      {total > limit && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-40">Prev</button>
          <span>Page {page} / {Math.ceil(total / limit)}</span>
          <button disabled={page >= Math.ceil(total / limit)} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-40">Next</button>
        </div>
      )}

      {showForm && (
        <GRNForm project={project} existing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
      )}
      {detail && (
        <GRNDetail project={project} grn={detail}
          onClose={() => setDetail(null)}
          onChanged={() => { openDetail(detail.id); load(); }}
          onEdit={() => { setEditing(detail); setDetail(null); setShowForm(true); }}
          onAmend={() => { setAmendId(detail.id); }} />
      )}
      {amendId && (
        <GrnAmendModal project={project} grnId={amendId}
          onClose={() => setAmendId(null)}
          onDone={() => { setAmendId(null); if (detail) openDetail(detail.id); load(); }} />
      )}
      {showCats && <GrnCategoryMasterModal onClose={() => setShowCats(false)} />}
    </div>
  );
}
